import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import DPFeedbackIconTooltip, {
  FEEDBACK_TYPES,
} from "../../../packages/DPFeedbackIconTooltip";
import WPTabs from "../../../packages/WPTabs";

import TooltipRenderer from "../../TooltipRenderer";
import { getElementStyles } from "./PassTabs.utils";

import { ELEMENTS_PER_PV_RANGE } from "../../../constants/global.constants";

const PassTabs = ({ selectedRODesign }) => {
  const { t } = useTranslation();
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    setActiveTabIndex(0);
  }, [selectedRODesign]);

  const passes = selectedRODesign?.roConfiguration?.passes || [];

  const passTabs = useMemo(() => {
    const noOfPasses = passes.length || 0;
    return Array.from({ length: noOfPasses }, (_, index) => ({
      id: `pass-${index + 1}`,
      title: t("importPlantWavePro.pass", { number: index + 1 }),
    }));
  }, [passes, t]);

  const stageGrid = (stage) => {
    const elementsPerPv = Number(stage?.noOfElements) || 0;
    const stageElements = stage?.roNfElements;

    const elementsList = [
      ...stageElements,
      ...new Array(elementsPerPv - stageElements.length).fill(stageElements[0]),
      ...new Array(ELEMENTS_PER_PV_RANGE.MAX - elementsPerPv),
    ];
    const elementStyles = getElementStyles(elementsList);

    return (
      <div className="wavepro-element-grid">
        {elementsList?.map((element, elementIndex) => {
          return (
            <div
              style={{ position: "relative" }}
              key={`element-container-${elementIndex}`}
            >
              <div
                key={`element-${elementIndex}`}
                className="wavepro-element-label"
                style={element !== "" ? elementStyles[elementIndex] : {}}
              >
                <TooltipRenderer title={element} placement="top">
                  {element}
                </TooltipRenderer>
              </div>
              {element === "" && (
                <DPFeedbackIconTooltip
                  type={FEEDBACK_TYPES.WARNING}
                  message={t("importPlantWavePro.experimentalWarning")}
                  placement="top"
                  className="wavepro-experimental-element-icon"
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <WPTabs
      tabs={passTabs}
      className="wavepro-preview-tabs pass-tabs"
      activeTabIndex={activeTabIndex}
      onTabChange={setActiveTabIndex}
    >
      {passTabs.map((passTab, passIndex) => {
        const selectedPass = passes?.[passIndex];
        const stages = selectedPass?.stages || [];

        return (
          <div
            key={`pass-tab-content-${passTab.id}`}
            className="wavepro-pass-content"
          >
            <div className="wavepro-stage-grid">
              {stages.map((stage, stageIndex) => (
                <div key={`stage-${stageIndex}`} className="wavepro-stage-item">
                  <div className="wavepro-stage-label">
                    {`S${stageIndex + 1}`}
                  </div>

                  <div className="vertical-line-divider" />

                  <div className="wavepro-pv-label">
                    PVs{" "}
                    <span className="numeric-value">
                      {stage?.pressureVessels ?? 0}
                    </span>
                  </div>

                  <div className="vertical-line-divider" />

                  <div className="wavepro-elements-per-pv-label">
                    {t("importPlantWavePro.elements")} / PV
                    <span className="numeric-value">
                      {stage?.noOfElements ?? 0}
                    </span>
                  </div>

                  <div className="vertical-line-divider" />

                  {stageGrid(stage)}

                  <div className="vertical-line-divider" />

                  <div className="wavepro-total-elements">
                    {t("ValueStrings.total")}
                    <span className="numeric-value">
                      {(stage?.pressureVessels ?? 0) *
                        (stage?.noOfElements ?? 0)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div>
              {t("importPlantWavePro.totalElementsForPass")}:{" "}
              <span className="numeric-value">
                {stages.reduce(
                  (total, stage) =>
                    total +
                    (stage?.pressureVessels ?? 0) * (stage?.noOfElements ?? 0),
                  0,
                )}
              </span>
            </div>
          </div>
        );
      })}
    </WPTabs>
  );
};

export default PassTabs;
